import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", sort_order: 0, is_active: true };

export default function AssignmentManagement() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/assignments");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (editing) await api.put(`/assignments/${editing.id}`, payload);
      else await api.post("/assignments", payload);
      toast.success("保存しました");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const move = async (it, dir) => {
    try {
      await api.put(`/assignments/${it.id}`, { sort_order: it.sort_order + dir });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (it) => {
    try {
      await api.put(`/assignments/${it.id}`, { is_active: !it.is_active });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startEdit = (it) => { setEditing(it); setForm({ name: it.name, sort_order: it.sort_order, is_active: it.is_active }); setOpen(true); };
  const startNew = () => { setEditing(null); setForm({ ...EMPTY, sort_order: items.length }); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="assignment-management">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">配置マスタ</h1>
          <p className="text-sm text-slate-500">「ホール」「キッチン」などの配置の種類を店舗ごとに管理できます。</p>
        </div>
        <Button data-testid="add-assignment" onClick={startNew} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1" />配置を追加
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs text-slate-600 w-16">順番</th>
              <th className="px-3 py-2 text-left text-xs text-slate-600">配置名</th>
              <th className="px-3 py-2 text-left text-xs text-slate-600 w-32">状態</th>
              <th className="px-3 py-2 text-right text-xs text-slate-600 w-40">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={`border-b border-slate-100 ${!it.is_active ? "opacity-50" : ""}`} data-testid={`assignment-row-${it.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs">{it.sort_order}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => move(it, -1)}><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => move(it, 1)}><ArrowDown className="w-3 h-3" /></Button>
                  </div>
                </td>
                <td className="px-3 py-2 font-medium">{it.name}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${it.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                    {it.is_active ? "有効" : "無効"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => toggle(it)} data-testid={`toggle-${it.id}`}>
                    {it.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`edit-${it.id}`}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="4" className="px-3 py-6 text-center text-slate-400">配置がまだ登録されていません</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "配置を編集" : "配置を追加"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">配置名</Label>
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="例: ホール" />
            </div>
            <div>
              <Label className="text-xs">並び順 (小さいほど上)</Label>
              <Input data-testid="form-sort" type="number" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button data-testid="save-assignment" onClick={save} className="bg-blue-600 hover:bg-blue-700">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
